import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  Dimensions,
  StyleSheet,
  Pressable,
  Platform,
} from 'react-native';
import { View, Text } from 'native-base';
import Carousel, {
  ICarouselInstance,
} from 'react-native-reanimated-carousel';
import Animated, {
  FadeIn,
  FadeOut,
  useSharedValue,
} from 'react-native-reanimated';

import { useAppContext } from '../API/Context-API/AppContext';
import { Portal } from 'react-native-paper';
import ExpandedImageHeader from './Components/ExpandedImageHeader';
import ExpandedImageSlide from './Components/ExpandedImageSlide';
import ExpandedImageArrows from './Components/ExpandedImageArrows';
import ExpandedImagePagination from './Components/ExpandedImagePagination';
import { useExpandedImageKeyboard } from './hooks/useExpandedImageKeyboard';
import { useBodyScrollLock } from './hooks/useBodyScrollLock';
import { createExpandedImageSliderStyles } from './Components/ustils';

type SliderImage = string | { uri: string };

interface ExpandedImageSliderProps {
  visible: boolean;
  images: SliderImage[];
  /** index in images */
  initialIndex?: number;
  onClose: () => void;
  /** show pagination dots */
  showIndicators?: boolean;
  /** allow closing by clicking on backdrop */
  enableBackdropClose?: boolean;
  /** optional title on top (for desktop/web) */
  title?: string;
}

const ExpandedImageSlider: React.FC<ExpandedImageSliderProps> = ({
  visible,
  images,
  initialIndex = 0,
  onClose,
  showIndicators = true,
  enableBackdropClose = true,
  title,
}) => {
  const { scaleAll, isWeb, currentWidth, isSmallVersion } = useAppContext();
  const carouselRef = useRef<ICarouselInstance | null>(null);
  const [currentIndex, setCurrentIndex] = useState(initialIndex);

  const progress = useSharedValue<number>(initialIndex);

  const [imageLayouts, setImageLayouts] = useState<
    Record<number, { x: number; width: number }>
  >({});

  // сбрасываем замеры при смене размеров
  useEffect(() => {
    setImageLayouts({});
  }, [scaleAll, currentWidth]);

  // Sync index when opener changes it
  useEffect(() => {
    if (visible) {
      setCurrentIndex(initialIndex);
      // jump to given index when opening
      requestAnimationFrame(() => {
        carouselRef.current?.scrollTo?.({
          index: initialIndex,
          animated: false,
        });
      });
    }
  }, [visible, initialIndex]);

  const handleClose = useCallback(() => {
    onClose?.();
  }, [onClose]);

  const goNext = useCallback(() => {
    carouselRef.current?.next?.();
  }, []);

  const goPrev = useCallback(() => {
    carouselRef.current?.prev?.();
  }, []);

  useExpandedImageKeyboard({
    visible,
    isWeb,
    onClose: handleClose,
    onNext: goNext,
    onPrev: goPrev,
  });

  useBodyScrollLock({
    enabled: visible,
    isWeb,
  });

  const styles = useMemo(
    () => createExpandedImageSliderStyles(scaleAll),
    [scaleAll],
  );

  if (!visible || !images || images.length === 0) {
    return null;
  }

  const data = images;
  const viewportWidth = currentWidth || Dimensions.get('window').width;
  const viewportHeight = Dimensions.get('window').height;
  const pageWidth = Math.min(viewportWidth, 1200);
  const pageHeight = viewportHeight;

  const onPressDot = (index: number) => {
    setCurrentIndex(index);
    carouselRef.current?.scrollTo?.({
      index,
      animated: true,
    });
  };

  const handleImageMeasure = useCallback((index: number, x: number, width: number) => {
    setImageLayouts((prev) => ({
      ...prev,
      [index]: { x, width },
    }));
  }, []);

  const currentImageLayout = imageLayouts[currentIndex];

  const arrowButtonSize = 40 * scaleAll;
  const safePadding = 16 * scaleAll;
  const distanceFromImage = 8 * scaleAll;

  // стрелка слева
  const arrowLeftOffset = (() => {
    if (!currentImageLayout) {
      return safePadding;
    }
    const imageLeft = currentImageLayout.x;
    const candidate = imageLeft - arrowButtonSize - distanceFromImage;
    // если картинка во всю ширину (imageLeft ≈ 0), будет использоваться safePadding
    return Math.max(candidate, safePadding);
  })();

  // const showKeyboardHint = React.useMemo( () => 
  // isWeb &&
  // data.length > 1 &&
  // (currentWidth || SCREEN_WIDTH) >= 900, [isWeb, data, currentWidth, SCREEN_WIDTH]); 


  // стрелка справа
  const arrowRightOffset = (() => {
    if (!currentImageLayout) {
      return safePadding;
    }
    const imageRight = currentImageLayout.x + currentImageLayout.width;
    const candidate = pageWidth - imageRight - arrowButtonSize - distanceFromImage;
    // если картинка во всю ширину (imageRight ≈ pageWidth), будет использоваться safePadding
    return Math.max(candidate, safePadding);
  })();

  
  return (
    <Portal>
      <View
        style={{
          ...StyleSheet.absoluteFillObject,
          width: viewportWidth,
          height: viewportHeight,
          zIndex: 9999,
          ...(Platform.OS === 'web'
            ? {
                position: 'fixed' as const,
                left: 0,
                top: 0,
              }
            : null),
        }}
      >
        <Animated.View
          entering={FadeIn.duration(200)}
          exiting={FadeOut.duration(200)}
          style={[StyleSheet.absoluteFill, {}, styles.backdropRoot]}
        >
          {/* Backdrop clickable area */}
          {enableBackdropClose && (
            <Pressable style={[StyleSheet.absoluteFill]} onPress={handleClose} />
          )}

          {/* Content */}
          <View style={styles.contentWrapper}>
            <ExpandedImageHeader
              title={title}
              currentIndex={currentIndex}
              total={data.length}
              scaleAll={scaleAll}
              onClose={handleClose}
              topBarStyle={styles.topBar}
              topBarLeftStyle={styles.topBarLeft}
              topBarRightStyle={styles.topBarRight}
              titleTextStyle={styles.titleText}
              counterTextStyle={styles.counterText}
              topBarIconWrapStyle={styles.topBarIconWrap}
            />

            {/* Carousel */}
            <View
              style={[
                styles.carouselWrapper,
                isSmallVersion && { width: '100%' },
              ]}
            >
              <View style={[styles.carouselInner, { width: pageWidth }]}>
                <Carousel
                  ref={carouselRef}
                  width={pageWidth}
                  height={pageHeight * 0.7}
                  data={data}
                  loop={data.length > 1}
                  defaultIndex={initialIndex}
                  pagingEnabled
                  style={styles.carousel}
                  onProgressChange={(_, absoluteProgress) => {
                    progress.value = absoluteProgress;
                  }}
                  onSnapToItem={index => {
                    setCurrentIndex(index);
                  }}
                  renderItem={({ item, index }) => {
                    const uri =
                      typeof item === 'string' ? item : item?.uri;

                    return (
                      <ExpandedImageSlide
                        uri={uri}
                        index={index}
                        pageWidth={pageWidth}
                        pageHeight={pageHeight}
                        scaleAll={scaleAll}
                        slideInnerStyle={styles.slideInner}
                        emptySlideStyle={styles.emptySlide}
                        onMeasure={handleImageMeasure}
                      />
                    );
                  }}
                />

                <ExpandedImageArrows
                  visible={data.length > 1}
                  scaleAll={scaleAll}
                  leftOffset={arrowLeftOffset}
                  rightOffset={arrowRightOffset}
                  onPrev={goPrev}
                  onNext={goNext}
                  leftWrapperStyle={styles.arrowLeftWrapper}
                  rightWrapperStyle={styles.arrowRightWrapper}
                  buttonStyle={styles.arrowButton}
                />
              </View>
            </View>

            <ExpandedImagePagination
              visible={showIndicators && data.length > 1}
              progress={progress}
              data={data}
              onPressDot={onPressDot}
              wrapperStyle={styles.paginationWrapper}
              containerStyle={styles.paginationContainer}
              dotStyle={styles.dotStyle}
              activeDotStyle={styles.activeDotStyle}
            />

            {/* Hint for controls on web/desktop */}
            {isWeb && viewportWidth > 900 && data && data.length > 0 &&  (
              <Text style={styles.hintText}>
                Нажмите ← → для переключения, Esc для закрытия
              </Text>
            )}
          </View>
        </Animated.View>
      </View>
    </Portal>
  );
};


export default React.memo(ExpandedImageSlider);
